import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Popover,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import {
  DatePicker
} from '@mui/x-date-pickers/DatePicker';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

// Authentication imports
import { useAuth } from '../../hooks/auth/useAuth';
import { envisFindingsApi, networksApi, classificationApi } from '../../services/api';

// Import the DefectValidationModal for viewing
import DefectValidationModal from '../Defects(ENVIS)/modals/DefectValidationModal';
import DisciplineAccessControl from '../common/DisciplineAccessControl';

const RoadAnalytics = () => {
  const theme = useTheme();
  const { user, getUserOrganization } = useAuth();
  
  // Get organization info from auth context
  const userOrganization = getUserOrganization();
  const organizationName = userOrganization?.name || '';
  const organizationId = userOrganization?.id;

  // State for chart data and controls
  const [dateRange, setDateRange] = useState('30');
  const [selectedNetwork, setSelectedNetwork] = useState('all');
  const [networks, setNetworks] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingNetworks, setLoadingNetworks] = useState(false);
  const [error, setError] = useState(null);

  // Master defect type filter state
  const [selectedDefectType, setSelectedDefectType] = useState('all');
  const [defectTypes, setDefectTypes] = useState([]);
  const [loadingDefectTypes, setLoadingDefectTypes] = useState(false);

  // Custom date range state
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);
  const [customFromDate, setCustomFromDate] = useState(null);
  const [customToDate, setCustomToDate] = useState(null);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);

  // KM Distribution Chart state (separate controls)
  const [kmDateRange, setKmDateRange] = useState('30');
  const [kmSelectedNetwork, setKmSelectedNetwork] = useState(''); // Will be set to NSE network ID when networks load
  const [kmChartData, setKmChartData] = useState([]);
  const [kmLoading, setKmLoading] = useState(false);
  const [kmError, setKmError] = useState(null);

  // KM Custom date range state
  const [kmIsCustomDateRange, setKmIsCustomDateRange] = useState(false);
  const [kmCustomFromDate, setKmCustomFromDate] = useState(null);
  const [kmCustomToDate, setKmCustomToDate] = useState(null);
  const [kmDatePickerAnchor, setKmDatePickerAnchor] = useState(null);

  // Table data state
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError, setTableError] = useState(null);
  const [selectedChartPoint, setSelectedChartPoint] = useState(null);

  // Modal state for viewing defect details
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRowDefect, setSelectedRowDefect] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Fetch networks for the organization
  const fetchNetworks = useCallback(async () => {
    if (!organizationId) return;

    setLoadingNetworks(true);
    try {
      const response = await networksApi.getAll({
        organization_id: organizationId
      });
      const networksList = response.data || [];
      setNetworks(networksList);
      
      // Set NSE as default for KM chart if available
      const nseNetwork = networksList.find(network => 
        network.name && network.name.toUpperCase().includes('NSE')
      );
      if (nseNetwork && !kmSelectedNetwork) {
        setKmSelectedNetwork(nseNetwork.id.toString());
      }
    } catch (err) {
      console.error('Failed to fetch networks:', err);
    } finally {
      setLoadingNetworks(false);
    }
  }, [organizationId, kmSelectedNetwork]);

  // Fetch defect types for the organization
  const fetchDefectTypes = useCallback(async () => {
    if (!organizationId) return;

    setLoadingDefectTypes(true);
    try {
      const response = await classificationApi.getDefectTypes({
        organization_id: organizationId,
        asset_type_id: 1 // Road asset type
      });
      setDefectTypes(response.data || []);
    } catch (err) {
      console.error('Failed to fetch defect types:', err);
    } finally {
      setLoadingDefectTypes(false);
    }
  }, [organizationId]);

  // Fetch findings data by date and network
  const fetchFindingsData = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);

    try {
      let fromDate;
      let toDate = new Date(); // Default to today

      if (isCustomDateRange) {
        // Use custom date range
        if (!customFromDate || !customToDate) {
          setError('Please select both start and end dates');
          return;
        }
        fromDate = new Date(customFromDate);
        toDate = new Date(customToDate);
        // Ensure toDate is end of day
        toDate.setHours(23, 59, 59, 999);
      } else {
        // Use predefined date range
        const daysBack = parseInt(dateRange);
        fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - daysBack);
      }
      
      // Build query parameters (no network_id support in API)
      const queryParams = {
        from_date: fromDate.toISOString().split('T')[0],
        limit: 10000, // Get all records for aggregation
        sort_by: 'first_detected_at',
        sort_order: 'ASC'
      };

      // Add to_date for custom range
      if (isCustomDateRange && toDate) {
        queryParams.to_date = toDate.toISOString().split('T')[0];
      }

      // Add defect type filter
      if (selectedDefectType !== 'all') {
        queryParams.defect_type_id = selectedDefectType;
      }
      
      // Fetch all findings for the date range (organization-level)
      const response = await envisFindingsApi.getTableFindings(queryParams);

      // Filter by network on the frontend if specific network is selected
      let filteredFindings = response.data.data;
      if (selectedNetwork !== 'all') {
        filteredFindings = response.data.data.filter(finding => {
          return finding.network_id && finding.network_id.toString() === selectedNetwork;
        });
      }

      // Aggregate data by date
      const findingsByDate = {};
      
      filteredFindings.forEach(finding => {
        const date = new Date(finding.first_detected_at).toLocaleDateString('en-CA'); // YYYY-MM-DD format
        findingsByDate[date] = (findingsByDate[date] || 0) + 1;
      });

      // Generate chart data for all dates in range (fill missing dates with 0)
      const chartData = [];
      
      if (isCustomDateRange) {
        // For custom date range, iterate through each day
        const currentDate = new Date(fromDate);
        while (currentDate <= toDate) {
          const dateString = currentDate.toLocaleDateString('en-CA');
          const displayDate = currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          
          chartData.push({
            date: displayDate,
            fullDate: dateString,
            findings: findingsByDate[dateString] || 0
          });
          
          currentDate.setDate(currentDate.getDate() + 1);
        }
      } else {
        // For predefined ranges, use the existing logic
        const daysBack = parseInt(dateRange);
        for (let i = daysBack - 1; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const dateString = date.toLocaleDateString('en-CA');
          const displayDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          
          chartData.push({
            date: displayDate,
            fullDate: dateString,
            findings: findingsByDate[dateString] || 0
          });
        }
      }

      setChartData(chartData);
    } catch (err) {
      console.error('Failed to fetch findings data:', err);
      setError('Failed to load findings data');
    } finally {
      setLoading(false);
    }
  }, [organizationId, dateRange, selectedNetwork, isCustomDateRange, customFromDate, customToDate, selectedDefectType]);

  // Fetch findings data by KM marker
  const fetchKmFindingsData = useCallback(async () => {
    if (!organizationId) return;

    setKmLoading(true);
    setKmError(null);

    try {
      let fromDate;
      let toDate = new Date(); // Default to today

      if (kmIsCustomDateRange) {
        // Use custom date range
        if (!kmCustomFromDate || !kmCustomToDate) {
          setKmError('Please select both start and end dates');
          return;
        }
        fromDate = new Date(kmCustomFromDate);
        toDate = new Date(kmCustomToDate);
        // Ensure toDate is end of day
        toDate.setHours(23, 59, 59, 999);
      } else {
        // Use predefined date range
        const daysBack = parseInt(kmDateRange);
        fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - daysBack);
      }
      
      // Build query parameters (no network_id support in API)
      const queryParams = {
        from_date: fromDate.toISOString().split('T')[0],
        limit: 10000, // Get all records for aggregation
        sort_by: 'first_detected_at',
        sort_order: 'ASC'
      };

      // Add to_date for custom range
      if (kmIsCustomDateRange && toDate) {
        queryParams.to_date = toDate.toISOString().split('T')[0];
      }

      // Add defect type filter
      if (selectedDefectType !== 'all') {
        queryParams.defect_type_id = selectedDefectType;
      }
      
      // Fetch all findings for the date range (organization-level)
      const response = await envisFindingsApi.getTableFindings(queryParams);

      // Filter by network on the frontend (always filter by selected network for KM chart)
      let filteredFindings = response.data.data;
      if (kmSelectedNetwork) {
        filteredFindings = response.data.data.filter(finding => {
          return finding.network_id && finding.network_id.toString() === kmSelectedNetwork;
        });
      }

      // Filter out findings without KM marker (out of boundary findings)
      const findingsWithKm = filteredFindings.filter(finding => {
        const hasKm = finding.chainage_segment_name && finding.km_marker !== null && finding.km_marker !== undefined;
        return hasKm;
      });

      // Aggregate data by KM marker
      const findingsByKm = {};
      
      findingsWithKm.forEach(finding => {
        const kmMarker = parseFloat(finding.km_marker);
        if (!isNaN(kmMarker)) {
          const kmKey = kmMarker.toFixed(2); // Round to 2 decimal places for grouping
          findingsByKm[kmKey] = (findingsByKm[kmKey] || 0) + 1;
        }
      });

      // Convert to chart data and sort by KM
      const chartData = Object.entries(findingsByKm)
        .map(([km, count]) => ({
          km: parseFloat(km),
          kmDisplay: `KM ${km}`,
          findings: count,
          x: parseFloat(km), // For scatter chart x-axis
          y: count // For scatter chart y-axis
        }))
        .sort((a, b) => a.km - b.km);

      setKmChartData(chartData);
    } catch (err) {
      console.error('Failed to fetch KM findings data:', err);
      setKmError('Failed to load KM distribution data');
    } finally {
      setKmLoading(false);
    }
  }, [organizationId, kmDateRange, kmSelectedNetwork, kmIsCustomDateRange, kmCustomFromDate, kmCustomToDate, selectedDefectType]);

  // Fetch networks when component mounts
  useEffect(() => {
    if (organizationId) {
      fetchNetworks();
    }
  }, [fetchNetworks]);

  // Fetch defect types when component mounts
  useEffect(() => {
    if (organizationId) {
      fetchDefectTypes();
    }
  }, [fetchDefectTypes]);

  // Fetch data when component mounts or filters change
  useEffect(() => {
    if (organizationId) {
      fetchFindingsData();
    }
  }, [fetchFindingsData]);

  // Fetch KM data when component mounts or KM filters change
  useEffect(() => {
    if (organizationId) {
      fetchKmFindingsData();
    }
  }, [fetchKmFindingsData]);

  // Handle date range change
  const handleDateRangeChange = (event, newDateRange) => {
    if (newDateRange !== null) {
      setDateRange(newDateRange);
      setIsCustomDateRange(false); // Reset custom date range when predefined is selected
      setCustomFromDate(null);
      setCustomToDate(null);
    }
  };

  // Handle network change
  const handleNetworkChange = (event) => {
    setSelectedNetwork(event.target.value);
  };

  // Handle custom date range
  const handleCustomDateRangeClick = (event) => {
    setDatePickerAnchor(event.currentTarget);
  };

  const handleCloseDatePicker = () => {
    setDatePickerAnchor(null);
  };

  const handleApplyCustomDateRange = () => {
    if (customFromDate && customToDate) {
      setIsCustomDateRange(true);
      setDateRange(''); // Clear predefined selection
      setDatePickerAnchor(null);
    }
  };

  const handleClearCustomDateRange = () => {
    setIsCustomDateRange(false);
    setCustomFromDate(null);
    setCustomToDate(null);
    setDateRange('30'); // Reset to default
    setDatePickerAnchor(null);
  };

  // Get date range display text
  const getDateRangeDisplay = () => {
    if (isCustomDateRange && customFromDate && customToDate) {
      const fromStr = customFromDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const toStr = customToDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${fromStr} - ${toStr}`;
    }
    return null;
  };

  // KM Chart event handlers
  const handleKmDateRangeChange = (event, newDateRange) => {
    if (newDateRange !== null) {
      setKmDateRange(newDateRange);
      setKmIsCustomDateRange(false); // Reset custom date range when predefined is selected
      setKmCustomFromDate(null);
      setKmCustomToDate(null);
    }
  };

  const handleKmNetworkChange = (event) => {
    setKmSelectedNetwork(event.target.value);
  };

  const handleKmCustomDateRangeClick = (event) => {
    setKmDatePickerAnchor(event.currentTarget);
  };

  const handleKmCloseDatePicker = () => {
    setKmDatePickerAnchor(null);
  };

  const handleKmApplyCustomDateRange = () => {
    if (kmCustomFromDate && kmCustomToDate) {
      setKmIsCustomDateRange(true);
      setKmDateRange(''); // Clear predefined selection
      setKmDatePickerAnchor(null);
    }
  };

  const handleKmClearCustomDateRange = () => {
    setKmIsCustomDateRange(false);
    setKmCustomFromDate(null);
    setKmCustomToDate(null);
    setKmDateRange('30'); // Reset to default
    setKmDatePickerAnchor(null);
  };

  const getKmDateRangeDisplay = () => {
    if (kmIsCustomDateRange && kmCustomFromDate && kmCustomToDate) {
      const fromStr = kmCustomFromDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const toStr = kmCustomToDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${fromStr} - ${toStr}`;
    }
    return null;
  };

  // Handle defect type filter change
  const handleDefectTypeChange = (event) => {
    setSelectedDefectType(event.target.value);
    // Clear table data when filter changes
    setTableData([]);
    setSelectedChartPoint(null);
  };

  // Fetch table data for selected chart point
  const fetchTableDataForPoint = useCallback(async (pointData, chartType) => {
    if (!organizationId) return;

    setTableLoading(true);
    setTableError(null);
    setSelectedChartPoint({ ...pointData, chartType });

    try {
      let queryParams = {
        limit: 1000, // Get all findings for the selected point
        sort_by: 'first_detected_at',
        sort_order: 'DESC'
      };

      if (chartType === 'timeline') {
        // For timeline chart, filter by specific date
        const selectedDate = pointData.fullDate; // YYYY-MM-DD format
        queryParams.from_date = selectedDate;
        queryParams.to_date = selectedDate;
      } else if (chartType === 'km') {
        // For KM chart, we need to get findings for that specific KM marker
        // Use the current KM chart date range
        let fromDate, toDate;
        
        if (kmIsCustomDateRange) {
          fromDate = new Date(kmCustomFromDate);
          toDate = new Date(kmCustomToDate);
          toDate.setHours(23, 59, 59, 999);
        } else {
          const daysBack = parseInt(kmDateRange);
          fromDate = new Date();
          fromDate.setDate(fromDate.getDate() - daysBack);
          toDate = new Date();
        }
        
        queryParams.from_date = fromDate.toISOString().split('T')[0];
        queryParams.to_date = toDate.toISOString().split('T')[0];
      }

      // Add defect type filter
      if (selectedDefectType !== 'all') {
        queryParams.defect_type_id = selectedDefectType;
      }

      // Fetch findings data
      const response = await envisFindingsApi.getTableFindings(queryParams);
      let findings = response.data.data;

      // Apply network filtering
      const networkFilter = chartType === 'timeline' ? selectedNetwork : kmSelectedNetwork;
      if (networkFilter && networkFilter !== 'all') {
        findings = findings.filter(finding => {
          return finding.network_id && finding.network_id.toString() === networkFilter;
        });
      }

      // For KM chart, filter by specific KM marker
      if (chartType === 'km') {
        const targetKm = pointData.km; // The KM value from the clicked point
        findings = findings.filter(finding => {
          const findingKm = parseFloat(finding.km_marker);
          return !isNaN(findingKm) && Math.abs(findingKm - targetKm) < 0.01; // Allow small precision differences
        });
      }

      // Transform findings data to match table structure
      const transformedData = findings.map(item => {
        const deviceName = item.device_name || item.device_uuid;
        const deviceDisplay = item.vehicle_id 
          ? `${deviceName} (${item.vehicle_id})` 
          : deviceName;

        const defectTypeName = item.validated_defect_type_name || item.defect_type_name || 'Unknown';

        let defectStatus = null;
        if (item.record_type === 'confirmed_defect' && item.defect_status) {
          defectStatus = item.defect_status;
        }

        let validationStatus = item.status;
        if (item.record_type === 'confirmed_defect') {
          validationStatus = 'validated';
        }

        const networkLocation = [
          item.network_name,
          item.section_name,
          item.chainage_segment_name
        ].filter(Boolean).join(' • ');
        
        const isOutOfBoundary = !networkLocation;
        const coordinatesDisplay = `${item.latitude?.toFixed(4)}, ${item.longitude?.toFixed(4)}`;
        
        let locationDisplay;
        if (isOutOfBoundary) {
          locationDisplay = `🚫 Out of Boundary • ${coordinatesDisplay}`;
        } else {
          locationDisplay = networkLocation;
        }

        return {
          id: item.id,
          recordType: item.record_type,
          defectId: item.defect_id,
          deviceId: deviceDisplay,
          location: locationDisplay,
          isOutOfBoundary: isOutOfBoundary,
          coordinates: coordinatesDisplay,
          defectType: defectTypeName,
          validationStatus: validationStatus,
          defectStatus: defectStatus,
          confidence: item.confidence_score,
          duplicationCount: item.duplication_count || 0,
          detectedAt: item.first_detected_at,
          createdAt: item.created_at || item.first_detected_at,
          kmMarker: item.km_marker,
          rawData: item
        };
      });

      setTableData(transformedData);
      setPage(0); // Reset to first page when new data is loaded
    } catch (err) {
      console.error('Failed to fetch table data:', err);
      setTableError('Failed to load detailed findings data');
    } finally {
      setTableLoading(false);
    }
  }, [organizationId, selectedNetwork, kmSelectedNetwork, kmDateRange, kmIsCustomDateRange, kmCustomFromDate, kmCustomToDate, selectedDefectType]);

  // Handle chart point clicks
  const handleTimelineChartClick = (data, index) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const pointData = data.activePayload[0].payload;
      fetchTableDataForPoint(pointData, 'timeline');
    }
  };

  const handleKmChartClick = (data, index) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const pointData = data.activePayload[0].payload;
      fetchTableDataForPoint(pointData, 'km');
    }
  };

  // Handle table row click to open view modal
  const handleRowClick = (rowData) => {
    // Transform the row data to match the format expected by DefectValidationModal
    const defectForModal = {
      id: rowData.id,
      recordType: rowData.recordType,
      defectId: rowData.defectId,
      defectType: rowData.defectType,
      confidence: rowData.confidence,
      detectedAt: rowData.detectedAt,
      location: rowData.location,
      rawData: rowData.rawData,
      originalBoundingBoxId: rowData.rawData?.original_bounding_box_id,
      // Additional fields that might be needed
      validatedBy: rowData.rawData?.validated_by_name,
      validatedAt: rowData.rawData?.validated_at,
    };
    
    setSelectedRowDefect(defectForModal);
    setViewModalOpen(true);
  };

  // Handle modal close
  const handleViewModalClose = () => {
    setViewModalOpen(false);
    setSelectedRowDefect(null);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get paginated data
  const paginatedTableData = tableData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <DisciplineAccessControl requiredDiscipline="Road Pavement">
    <>
      {/* Master Defect Type Filter */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          mb: 3,
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Master Filter
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Filter all analytics by defect type
            </Typography>
          </Box>
          
          <Box sx={{ flex: 1, minWidth: 250, maxWidth: 400 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Defect Type Filter</InputLabel>
              <Select
                value={selectedDefectType}
                label="Defect Type Filter"
                onChange={handleDefectTypeChange}
                disabled={loadingDefectTypes}
                sx={{
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  }
                }}
              >
                <MenuItem value="all">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      All Defect Types
                    </Typography>
                  </Box>
                </MenuItem>
                {defectTypes.map((defectType) => (
                  <MenuItem key={defectType.id} value={defectType.id.toString()}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {defectType.name}
                      </Typography>
                      {defectType.description && (
                        <Typography variant="caption" color="text.secondary">
                          {defectType.description}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {selectedDefectType !== 'all' && (
            <Chip
              label={defectTypes.find(dt => dt.id.toString() === selectedDefectType)?.name || 'Selected Type'}
              color="primary"
              variant="filled"
              onDelete={() => setSelectedDefectType('all')}
              sx={{ fontWeight: 500 }}
            />
          )}
        </Box>
      </Paper>

      {/* Findings Chart Section */}
      <Paper 
        sx={{ 
          p: 3, 
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          mb: 3
        }}
      >
        {/* Chart Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Findings Over Time
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Daily count of AI-detected findings from ENViS devices
              </Typography>
            </Box>
          </Box>
          
          {/* Clean Controls Row */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            flexWrap: 'wrap',
            p: 2,
            bgcolor: alpha(theme.palette.grey[50], 0.5),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`
          }}>
            {/* Network Selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', minWidth: 'fit-content' }}>
                Network:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={selectedNetwork}
                  onChange={handleNetworkChange}
                  disabled={loadingNetworks}
                  displayEmpty
                  sx={{
                    borderRadius: '6px',
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                    }
                  }}
                >
                  <MenuItem value="all">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      All Networks
                    </Typography>
                  </MenuItem>
                  {networks.map((network) => (
                    <MenuItem key={network.id} value={network.id.toString()}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {network.name}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Date Range Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'flex-end' }}>
              {isCustomDateRange && getDateRangeDisplay() ? (
                <Chip
                  label={getDateRangeDisplay()}
                  onDelete={handleClearCustomDateRange}
                  color="primary"
                  variant="filled"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              ) : (
                <ToggleButtonGroup
                  value={dateRange}
                  exclusive
                  onChange={handleDateRangeChange}
                  size="small"
                  sx={{
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                    borderRadius: '6px',
                    bgcolor: 'white',
                    overflow: 'hidden',
                    '& .MuiToggleButton-root': {
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      border: 'none',
                      borderRadius: 0,
                      borderRight: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`,
                      bgcolor: 'transparent',
                      color: 'text.secondary',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                        }
                      },
                      '&:hover:not(.Mui-selected)': {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      }
                    }
                  }}
                >
                  <ToggleButton value="30">LAST 30 DAYS</ToggleButton>
                  <ToggleButton value="60">LAST 60 DAYS</ToggleButton>
                  <ToggleButton value="90">LAST 90 DAYS</ToggleButton>
                </ToggleButtonGroup>
              )}
              
              <Button
                variant="outlined"
                size="small"
                startIcon={<CalendarTodayIcon sx={{ fontSize: '16px' }} />}
                onClick={handleCustomDateRangeClick}
                sx={{
                  px: 2,
                  py: 0.5,
                  fontSize: '0.75rem',
                  borderRadius: '6px',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                  bgcolor: 'white',
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    borderColor: theme.palette.primary.main,
                  }
                }}
              >
                Custom Range
              </Button>
            </Box>

            {/* Date Range Picker Popover */}
            <Popover
              open={Boolean(datePickerAnchor)}
              anchorEl={datePickerAnchor}
              onClose={handleCloseDatePicker}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  p: 3,
                  mt: 1,
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                  minWidth: 320,
                  bgcolor: 'white'
                }
              }}
            >
              <Stack spacing={2.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                  Select Custom Date Range
                </Typography>
                
                <Stack spacing={2}>
                  <DatePicker
                    label="From Date"
                    value={customFromDate}
                    onChange={setCustomFromDate}
                    maxDate={customToDate || new Date()}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            bgcolor: 'white',
                            '& fieldset': {
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: theme.palette.primary.main,
                              borderWidth: '1px',
                            }
                          }
                        }
                      }
                    }}
                  />
                  
                  <DatePicker
                    label="To Date"
                    value={customToDate}
                    onChange={setCustomToDate}
                    minDate={customFromDate}
                    maxDate={new Date()}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            bgcolor: 'white',
                            '& fieldset': {
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: theme.palette.primary.main,
                              borderWidth: '1px',
                            }
                          }
                        }
                      }
                    }}
                  />
                </Stack>
                
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1.5, 
                  justifyContent: 'flex-end'
                }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCloseDatePicker}
                    sx={{
                      px: 2.5,
                      py: 0.75,
                      fontSize: '0.75rem',
                      borderRadius: '6px',
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                      bgcolor: 'white',
                      color: 'text.secondary',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.grey[500], 0.04),
                        borderColor: theme.palette.grey[400],
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleApplyCustomDateRange}
                    disabled={!customFromDate || !customToDate}
                    sx={{
                      px: 2.5,
                      py: 0.75,
                      fontSize: '0.75rem',
                      borderRadius: '6px',
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                      },
                      '&.Mui-disabled': {
                        bgcolor: alpha(theme.palette.grey[400], 0.3),
                        color: 'text.disabled',
                      }
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Stack>
            </Popover>
          </Box>
        </Box>

        {/* Chart Content */}
        <Box sx={{ height: 400, width: '100%' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {!loading && !error && chartData.length > 0 && (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart 
                data={chartData} 
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                onClick={handleTimelineChartClick}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.grey[500], 0.3)} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  tickLine={{ stroke: theme.palette.grey[300] }}
                  axisLine={{ stroke: theme.palette.grey[300] }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  tickLine={{ stroke: theme.palette.grey[300] }}
                  axisLine={{ stroke: theme.palette.grey[300] }}
                  label={{ 
                    value: 'Number of Findings', 
                    angle: -90, 
                    position: 'insideLeft',
                    style: { textAnchor: 'middle', fill: theme.palette.text.secondary }
                  }}
                />
                <Tooltip content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    return (
                      <Paper sx={{ p: 2, boxShadow: theme.shadows[3] }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {label}
                        </Typography>
                        <Typography variant="body2" color="primary">
                          {`Findings: ${payload[0].value}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click to view details
                        </Typography>
                      </Paper>
                    );
                  }
                  return null;
                }} />
                <Line 
                  type="monotone" 
                  dataKey="findings" 
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  dot={{ 
                    fill: theme.palette.primary.main, 
                    strokeWidth: 2, 
                    r: 4,
                    cursor: 'pointer'
                  }}
                  activeDot={{ 
                    r: 6, 
                    stroke: theme.palette.primary.main, 
                    strokeWidth: 2,
                    cursor: 'pointer'
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
          
          {!loading && !error && chartData.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                No findings data available for the selected period
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* KM Distribution Chart Section */}
      <Paper 
        sx={{ 
          p: 3, 
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          mb: 3
        }}
      >
        {/* KM Chart Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Findings by KM Marker
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Distribution of findings along the road network by KM markers
              </Typography>
            </Box>
          </Box>
          
          {/* Clean Controls Row */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            flexWrap: 'wrap',
            p: 2,
            bgcolor: alpha(theme.palette.grey[50], 0.5),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`
          }}>
            {/* Network Selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', minWidth: 'fit-content' }}>
                Network:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={kmSelectedNetwork}
                  onChange={handleKmNetworkChange}
                  disabled={loadingNetworks}
                  displayEmpty
                  sx={{
                    borderRadius: '6px',
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                    }
                  }}
                >
                  {networks.map((network) => (
                    <MenuItem key={network.id} value={network.id.toString()}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {network.name}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Date Range Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'flex-end' }}>
              {kmIsCustomDateRange && getKmDateRangeDisplay() ? (
                <Chip
                  label={getKmDateRangeDisplay()}
                  onDelete={handleKmClearCustomDateRange}
                  color="primary"
                  variant="filled"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              ) : (
                <ToggleButtonGroup
                  value={kmDateRange}
                  exclusive
                  onChange={handleKmDateRangeChange}
                  size="small"
                  sx={{
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                    borderRadius: '6px',
                    bgcolor: 'white',
                    overflow: 'hidden',
                    '& .MuiToggleButton-root': {
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      border: 'none',
                      borderRadius: 0,
                      borderRight: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`,
                      bgcolor: 'transparent',
                      color: 'text.secondary',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                        }
                      },
                      '&:hover:not(.Mui-selected)': {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      }
                    }
                  }}
                >
                  <ToggleButton value="30">LAST 30 DAYS</ToggleButton>
                  <ToggleButton value="60">LAST 60 DAYS</ToggleButton>
                  <ToggleButton value="90">LAST 90 DAYS</ToggleButton>
                </ToggleButtonGroup>
              )}
              
              <Button
                variant="outlined"
                size="small"
                startIcon={<CalendarTodayIcon sx={{ fontSize: '16px' }} />}
                onClick={handleKmCustomDateRangeClick}
                sx={{
                  px: 2,
                  py: 0.5,
                  fontSize: '0.75rem',
                  borderRadius: '6px',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                  bgcolor: 'white',
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    borderColor: theme.palette.primary.main,
                  }
                }}
              >
                Custom Range
              </Button>
            </Box>

            {/* KM Date Range Picker Popover */}
            <Popover
              open={Boolean(kmDatePickerAnchor)}
              anchorEl={kmDatePickerAnchor}
              onClose={handleKmCloseDatePicker}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  p: 3,
                  mt: 1,
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                  minWidth: 320,
                  bgcolor: 'white'
                }
              }}
            >
              <Stack spacing={2.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                  Select Custom Date Range
                </Typography>
                
                <Stack spacing={2}>
                  <DatePicker
                    label="From Date"
                    value={kmCustomFromDate}
                    onChange={setKmCustomFromDate}
                    maxDate={kmCustomToDate || new Date()}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            bgcolor: 'white',
                            '& fieldset': {
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: theme.palette.primary.main,
                              borderWidth: '1px',
                            }
                          }
                        }
                      }
                    }}
                  />
                  
                  <DatePicker
                    label="To Date"
                    value={kmCustomToDate}
                    onChange={setKmCustomToDate}
                    minDate={kmCustomFromDate}
                    maxDate={new Date()}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            bgcolor: 'white',
                            '& fieldset': {
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: theme.palette.primary.main,
                              borderWidth: '1px',
                            }
                          }
                        }
                      }
                    }}
                  />
                </Stack>
                
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1.5, 
                  justifyContent: 'flex-end'
                }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleKmCloseDatePicker}
                    sx={{
                      px: 2.5,
                      py: 0.75,
                      fontSize: '0.75rem',
                      borderRadius: '6px',
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                      bgcolor: 'white',
                      color: 'text.secondary',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.grey[500], 0.04),
                        borderColor: theme.palette.grey[400],
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleKmApplyCustomDateRange}
                    disabled={!kmCustomFromDate || !kmCustomToDate}
                    sx={{
                      px: 2.5,
                      py: 0.75,
                      fontSize: '0.75rem',
                      borderRadius: '6px',
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                      },
                      '&.Mui-disabled': {
                        bgcolor: alpha(theme.palette.grey[400], 0.3),
                        color: 'text.disabled',
                      }
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Stack>
            </Popover>
          </Box>
        </Box>

        {/* KM Chart Content */}
        <Box sx={{ height: 400, width: '100%' }}>
          {kmLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          )}
          
          {kmError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {kmError}
            </Alert>
          )}
          
          {!kmLoading && !kmError && kmChartData.length > 0 && (
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart 
                data={kmChartData} 
                margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                onClick={handleKmChartClick}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.grey[500], 0.3)} />
                <XAxis 
                  type="number"
                  dataKey="x"
                  domain={['dataMin', 'dataMax']}
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  tickLine={{ stroke: theme.palette.grey[300] }}
                  axisLine={{ stroke: theme.palette.grey[300] }}
                  label={{ 
                    value: 'KM Marker', 
                    position: 'insideBottom', 
                    offset: -10,
                    style: { textAnchor: 'middle', fill: theme.palette.text.secondary }
                  }}
                  tickFormatter={(value) => `KM ${value.toFixed(1)}`}
                />
                <YAxis 
                  type="number"
                  dataKey="y"
                  domain={[0, 'dataMax']}
                  allowDecimals={false}
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  tickLine={{ stroke: theme.palette.grey[300] }}
                  axisLine={{ stroke: theme.palette.grey[300] }}
                  label={{ 
                    value: 'Number of Findings', 
                    angle: -90, 
                    position: 'insideLeft',
                    style: { textAnchor: 'middle', fill: theme.palette.text.secondary }
                  }}
                />
                <Tooltip content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <Paper sx={{ p: 2, boxShadow: theme.shadows[3] }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {data.kmDisplay}
                        </Typography>
                        <Typography variant="body2" color="primary">
                          {`Findings: ${data.findings}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click to view details
                        </Typography>
                      </Paper>
                    );
                  }
                  return null;
                }} />
                <Scatter 
                  fill={alpha(theme.palette.secondary.main, 0.7)}
                  stroke={theme.palette.secondary.main}
                  strokeWidth={1}
                  cursor="pointer"
                  shape={(props) => {
                    const { cx, cy, payload } = props;
                    // Dynamic size based on findings count for better visual hierarchy
                    const baseSize = 2;
                    const maxSize = 5;
                    const size = Math.min(maxSize, baseSize + (payload.findings * 0.3));
                    
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={size}
                        fill={alpha(theme.palette.secondary.main, 0.8)}
                        stroke={theme.palette.secondary.main}
                        strokeWidth={1.5}
                        style={{
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.setAttribute('r', size + 2);
                          e.target.setAttribute('fill', theme.palette.secondary.main);
                        }}
                        onMouseLeave={(e) => {
                          e.target.setAttribute('r', size);
                          e.target.setAttribute('fill', alpha(theme.palette.secondary.main, 0.8));
                        }}
                      />
                    );
                  }}
                />
              </ScatterChart>
            </ResponsiveContainer>
          )}
          
          {!kmLoading && !kmError && kmChartData.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                No findings with KM markers available for the selected period and network
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Defects Table Section */}
      <Paper 
        sx={{ 
          p: 3, 
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          mb: 3
        }}
      >
        {/* Table Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Defects Data
              {selectedChartPoint && (
                <Chip 
                  label={
                    selectedChartPoint.chartType === 'timeline' 
                      ? `${selectedChartPoint.date} (${selectedChartPoint.findings} findings)`
                      : `${selectedChartPoint.kmDisplay} (${selectedChartPoint.findings} findings)`
                  }
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ ml: 2 }}
                  onDelete={() => {
                    setSelectedChartPoint(null);
                    setTableData([]);
                  }}
                />
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedChartPoint 
                ? `Showing findings for ${selectedChartPoint.chartType === 'timeline' ? 'selected date' : 'selected KM marker'}`
                : 'Click on chart points above to view detailed findings data'
              }
            </Typography>
          </Box>
        </Box>

        {/* Table Content */}
        <TableContainer 
          component={Paper} 
          sx={{ 
            borderRadius: '12px',
            boxShadow: theme.shadows[2],
            overflow: 'hidden',
            mb: 3
          }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="defects table">
            <TableHead>
              <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Defect Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Validation Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Defect Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '280px', minWidth: '280px' }}>Time Progress</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableLoading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Loading findings data...
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              
              {tableError && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Alert severity="error" sx={{ maxWidth: 400, mx: 'auto' }}>
                      {tableError}
                    </Alert>
                  </TableCell>
                </TableRow>
              )}
              
              {!tableLoading && !tableError && tableData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {selectedChartPoint 
                        ? 'No findings data found for the selected point' 
                        : 'Click on chart points above to view detailed findings data'
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              
              {!tableLoading && !tableError && tableData.length > 0 && paginatedTableData.map((row, index) => (
                <TableRow
                  key={`${row.recordType}-${row.id}-${index}`}
                  onClick={() => handleRowClick(row)}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      cursor: 'pointer',
                    },
                    bgcolor: row.recordType === 'confirmed_defect' 
                      ? alpha(theme.palette.success.main, 0.02) 
                      : 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  {/* ID Column */}
                  <TableCell>
                    <Chip 
                      label={row.recordType === 'confirmed_defect' ? `D-${row.defectId}` : `F-${row.id}`}
                      size="small"
                      sx={{
                        bgcolor: row.recordType === 'confirmed_defect' 
                          ? alpha(theme.palette.success.main, 0.1)
                          : alpha(theme.palette.info.main, 0.1),
                        color: row.recordType === 'confirmed_defect' 
                          ? theme.palette.success.main
                          : theme.palette.info.main,
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  
                  {/* Source Column */}
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                        {row.deviceId}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                        📍 {row.location}
                      </Typography>
                      {row.kmMarker && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                          🛣️ KM {parseFloat(row.kmMarker).toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  
                  {/* Defect Type Column */}
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                        {row.defectType}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                        {new Date(row.detectedAt).toLocaleString()}
                      </Typography>
                      {row.confidence && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                          🎯 {(row.confidence * 100).toFixed(1)}% confidence
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  
                  {/* Validation Status Column */}
                  <TableCell>
                    <Chip
                      label={row.validationStatus || 'Pending'}
                      size="small"
                      color={
                        row.validationStatus === 'validated' ? 'success' :
                        row.validationStatus === 'rejected' ? 'error' : 'default'
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  
                  {/* Defect Status Column */}
                  <TableCell>
                    {row.defectStatus ? (
                      <Chip
                        label={row.defectStatus}
                        size="small"
                        color={
                          row.defectStatus === 'resolved' ? 'success' :
                          row.defectStatus === 'in_progress' ? 'warning' : 'info'
                        }
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  
                  {/* Time Progress Column */}
                  <TableCell sx={{ width: '280px', minWidth: '280px' }}>
                    <Typography variant="caption" color="text.secondary">
                      Created: {new Date(row.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!tableLoading && !tableError && tableData.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={tableData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                backgroundColor: alpha(theme.palette.grey[50], 0.3),
                '& .MuiTablePagination-toolbar': {
                  minHeight: '52px',
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.875rem',
                  color: theme.palette.text.secondary,
                },
                '& .MuiTablePagination-select': {
                  fontSize: '0.875rem',
                },
                '& .MuiTablePagination-actions': {
                  color: theme.palette.text.secondary,
                }
              }}
            />
          )}
        </TableContainer>
      </Paper>

      {/* Placeholder for future sections */}
      <Paper 
        sx={{ 
          p: 4, 
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          textAlign: 'center',
          minHeight: '200px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
          More Analytics Coming Soon
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500 }}>
          Future sections will include device performance metrics, defect type distributions, 
          SLA compliance reports, and predictive insights for proactive asset management.
        </Typography>
      </Paper>

      {/* View-only modal for defect details */}
      {selectedRowDefect && (
        <DefectValidationModal
          open={viewModalOpen}
          onClose={handleViewModalClose}
          selectedDefect={selectedRowDefect}
          defectTypesList={defectTypes}
          currentDefectTypeId={selectedRowDefect.rawData?.defect_type_id || selectedRowDefect.rawData?.validated_defect_type_id}
          onDefectTypeChange={() => {}} // No-op for view-only
          rejectionReason=""
          onRejectionReasonChange={() => {}} // No-op for view-only
          loadingDefectTypes={false}
          onValidate={() => {}} // No-op for view-only
          onReject={() => {}} // No-op for view-only
          formatDateTime={(date) => {
            if (!date) return 'N/A';
            return new Date(date).toLocaleString();
          }}
          viewOnly={true} // This makes the modal read-only
        />
      )}
    </>
    </DisciplineAccessControl>
  );
};

export default RoadAnalytics;